import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProfile, saveProfileToAPI, generateFromWebsite, generateFromText } from '../services/profileService';
import { extractPdfText } from '../services/pdfText';
import { SIZE_MIN_OPTS, SIZE_MAX_OPTS, normalizeCompanySize } from '../lib/companySizeRange';
import styles from './SellerProfilePage.module.css';

// ─── Industry options - keys match INDUSTRY_TRANSLATION_MAP in backend/server.js exactly ──
// Grouped for display; the stored value is always the map key so the API always finds a match.
export const INDUSTRY_OPTIONS = [
  // Tech verticals
  { key: 'saas',                   label: 'SaaS' },
  { key: 'fintech',                label: 'Fintech' },
  { key: 'healthtech',             label: 'Healthtech' },
  { key: 'cybersecurity',          label: 'Cybersecurity' },
  { key: 'cloud',                  label: 'Cloud' },
  { key: 'artificial intelligence',label: 'AI / Machine Learning' },
  { key: 'biotech',                label: 'Biotech' },
  { key: 'edtech',                 label: 'Edtech' },
  { key: 'proptech',               label: 'Proptech' },
  { key: 'insurtech',              label: 'Insurtech' },
  { key: 'martech',                label: 'Martech' },
  { key: 'legaltech',              label: 'Legaltech' },
  { key: 'hrtech',                 label: 'HR Tech' },
  { key: 'adtech',                 label: 'AdTech' },
  { key: 'cleantech',              label: 'Cleantech' },
  { key: 'regtech',                label: 'RegTech' },
  { key: 'wealthtech',             label: 'WealthTech' },
  { key: 'blockchain',             label: 'Blockchain' },
  // Traditional industries
  { key: 'healthcare',             label: 'Healthcare' },
  { key: 'finance',                label: 'Finance' },
  { key: 'technology',             label: 'Technology' },
  { key: 'real estate',            label: 'Real Estate' },
  { key: 'insurance',              label: 'Insurance' },
  { key: 'retail',                 label: 'Retail' },
  { key: 'manufacturing',          label: 'Manufacturing' },
  { key: 'logistics',              label: 'Logistics' },
  { key: 'media',                  label: 'Media' },
];

export const PROFILE_KEY = 'repbuddy_seller_profile';

export function loadSellerProfile() {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || 'null'); }
  catch { return null; }
}

function saveSellerProfile(p) {
  try { localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); } catch {}
}

// Proof points persist as one newline-separated string (the shape the backend expects), but the
// form edits them as individual rows. splitProof turns the stored string into that row array,
// always leaving at least one (empty) row so there's a field to type into.
function splitProof(str) {
  const lines = String(str || '').split('\n').map(s => s.trim()).filter(Boolean);
  return lines.length ? lines : [''];
}

export default function SellerProfilePage() {
  const navigate = useNavigate();
  const [logoOk, setLogoOk] = useState(true); // header logo image → lettermark fallback

  // ── Seller Info ───────────────────────────────────────────────────────────
  const [name,      setName]      = useState('');
  const [company,   setCompany]   = useState('');
  const [title,     setTitle]     = useState('');
  const [valueProp, setValueProp] = useState('');
  const [website,   setWebsite]   = useState('');

  // ── Auto-generate (website / PDF -> value prop + proof points) ─────────────
  const [genLoading, setGenLoading] = useState(false);
  const [genErr,     setGenErr]     = useState('');
  const [genNote,    setGenNote]    = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfErr,     setPdfErr]     = useState('');
  const [pdfNote,    setPdfNote]    = useState('');
  const pdfInputRef = useRef(null);

  // ── ICP ───────────────────────────────────────────────────────────────────
  const [industries,     setIndustries]     = useState([]);
  const [jobTitles,      setJobTitles]      = useState([]);
  const [titlesInput,    setTitlesInput]    = useState('');
  const [companySizeMin, setCompanySizeMin] = useState('');
  const [companySizeMax, setCompanySizeMax] = useState('');

  // ── Proof points ──────────────────────────────────────────────────────────
  const [proofList, setProofList] = useState(['']);

  // ── Load saved profile on mount ───────────────────────────────────────────
  // Populate from localStorage immediately (instant), then sync from API.
  function applyProfile(p) {
    if (!p) return;
    setName(p.name       || '');
    setCompany(p.company || '');
    setTitle(p.title     || '');
    setValueProp(p.valueProp || '');
    setWebsite(p.website || '');
    // Only keep industries that correspond to a valid chip key - silently drops any
    // free-text values saved by the old tag-input UI so they don't become invisible state.
    const validKeys = new Set(INDUSTRY_OPTIONS.map(o => o.key));
    setIndustries((p.icp?.industries || []).filter(k => validKeys.has(k)));
    setJobTitles(p.icp?.titles         || []);

    // Company size - stored as "min,max". Normalizing also reads a "51-200" the chat card
    // used to save, which otherwise loaded as blank.
    const [csMin = '', csMax = ''] = normalizeCompanySize(p.icp?.companySize).split(',');
    setCompanySizeMin(csMin);
    setCompanySizeMax(csMax);

    setProofList(splitProof(p.proofPoints));
  }

  useEffect(() => {
    // Fast path: localStorage (synchronous, no flicker)
    applyProfile(loadSellerProfile());

    // Slow path: API (may be newer if user changed device)
    fetchProfile().then(apiProfile => {
      if (apiProfile) {
        saveSellerProfile(apiProfile); // keep localStorage in sync
        applyProfile(apiProfile);
      }
    });
  }, []); // eslint-disable-line

  // ── Industry chip toggle ──────────────────────────────────────────────────
  function toggleIndustry(key) {
    setIndustries(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  }

  // ── Tag input handlers ────────────────────────────────────────────────────
  function handleTitlesKey(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = titlesInput.trim().replace(/,+$/, '').trim();
      if (val && !jobTitles.includes(val)) setJobTitles(p => [...p, val]);
      setTitlesInput('');
    } else if (e.key === 'Backspace' && titlesInput === '') {
      setJobTitles(p => p.slice(0, -1));
    }
  }

  function handleTitlesPaste(e) {
    const pasted = e.clipboardData.getData('text');
    if (!pasted.includes(',')) return; // single value - let browser handle normally
    e.preventDefault();
    const vals = pasted.split(',').map(v => v.trim()).filter(Boolean);
    setJobTitles(prev => {
      const next = [...prev];
      vals.forEach(v => { if (!next.includes(v)) next.push(v); });
      return next;
    });
    setTitlesInput('');
  }

  // ── Clear ─────────────────────────────────────────────────────────────────
  function clearForm() {
    setName(''); setCompany(''); setTitle(''); setValueProp(''); setWebsite('');
    setGenErr(''); setGenNote(''); setPdfErr(''); setPdfNote('');
    setIndustries([]); setJobTitles([]);
    setCompanySizeMin(''); setCompanySizeMax('');
    setProofList(['']);
  }

  // ── Auto-generate value prop + proof points ───────────────────────────────
  // Merge a generated draft into the form. Value prop is replaced only on an explicit website
  // generate (the user asked for a fresh draft); proof points are always merged additively and
  // de-duped by line, so a second source (a PDF after the website) adds to what's there rather
  // than wiping it. Nothing is saved here - the user reviews, edits, then clicks Save.
  function applyGenerated(res, { replaceValueProp } = {}) {
    if (res.valueProp && (replaceValueProp || !valueProp.trim())) setValueProp(res.valueProp);
    if (res.proofPoints) {
      setProofList(prev => {
        const existing = prev.map(s => s.trim()).filter(Boolean);
        const have     = new Set(existing.map(s => s.toLowerCase()));
        const added    = res.proofPoints.split('\n').map(s => s.trim())
          .filter(s => s && !have.has(s.toLowerCase()));
        const merged   = [...existing, ...added];
        return merged.length ? merged : [''];
      });
    }
  }

  // Proof-point row editing (the list is edited in place, joined to a string only on save).
  const updateProof = (i, val) => setProofList(list => list.map((p, idx) => (idx === i ? val : p)));
  const addProof    = () => setProofList(list => [...list, '']);
  const removeProof = (i) => setProofList(list => {
    const next = list.filter((_, idx) => idx !== i);
    return next.length ? next : ['']; // always leave one row to type into
  });

  async function handleGenerateFromWebsite() {
    const site = website.trim();
    if (!site || genLoading) return;
    setGenErr(''); setGenNote(''); setGenLoading(true);
    try {
      const res = await generateFromWebsite(site, company.trim());
      applyGenerated(res, { replaceValueProp: true });
      // When the page had no readable proof (common for a landing page), say so and point to the
      // PDF drop, rather than leaving an empty Proof Points field looking like a failure.
      const noProof = !res.proofPoints
        ? ' No proof points were readable on the page, so that field was left empty (nothing invented) - drop a case-study PDF below to add real ones.'
        : '';
      if (res.source === 'website') {
        const n = res.pages?.length || 0;
        setGenNote(`Drafted from your site${n ? ` (${n} page${n > 1 ? 's' : ''})` : ''}. Review and edit before saving, keeping only claims you can back up.${noProof}`);
      } else if (res.source === 'web-search') {
        setGenNote(`That site was hard to read directly, so this is drafted from web research. Double-check every line before saving.${noProof}`);
      } else {
        setGenNote("Couldn't read enough from that site. Try a different URL, drop a case-study PDF below, or write the fields in yourself.");
      }
    } catch (e) {
      setGenErr(e.message || 'Generation failed. Try again.');
    } finally {
      setGenLoading(false);
    }
  }

  async function handlePdfFile(file) {
    if (!file || pdfLoading) return;
    setPdfErr(''); setPdfNote('');
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) {
      setPdfErr('Please drop a PDF file.');
      return;
    }
    setPdfLoading(true);
    try {
      const text = await extractPdfText(file);
      if (!text || text.trim().length < 20) {
        setPdfErr("Couldn't read text from that PDF (it may be scanned images). Try another, or type your proof points in.");
        return;
      }
      const res = await generateFromText(text, company.trim());
      applyGenerated(res, { replaceValueProp: false });
      setPdfNote(res.proofPoints
        ? 'Pulled proof points from your PDF. Review and edit before saving.'
        : 'No concrete, verifiable results were in that PDF, so nothing was added rather than inventing claims.');
    } catch (e) {
      setPdfErr(e.message || 'Could not read that PDF.');
    } finally {
      setPdfLoading(false);
    }
  }

  function handlePdfDrop(e) {
    e.preventDefault();
    handlePdfFile(e.dataTransfer.files?.[0]);
  }

  function handlePdfInput(e) {
    handlePdfFile(e.target.files?.[0]);
    e.target.value = ''; // let the same file be re-selected
  }

  // ── Save & navigate ───────────────────────────────────────────────────────
  async function handleSave() {
    if (!name.trim() || !company.trim()) {
      alert('Please enter your name and company.');
      return;
    }
    if (!title.trim())     { alert('Please enter your title.');             return; }
    if (!valueProp.trim()) { alert('Please enter your value proposition.'); return; }

    const profileData = {
      name:     name.trim(),
      company:  company.trim(),
      title:    title.trim(),
      valueProp: valueProp.trim(),
      website:  website.trim(),
      // Only what the chat and search actually read. Tone, phrasing, exclusions, location and
      // seniority are said in chat per request, so the page no longer saves them. The whole
      // object is rewritten on save, so older keys drop off here too.
      icp: {
        industries,
        titles:      jobTitles,
        companySize: companySizeMin || companySizeMax ? `${companySizeMin},${companySizeMax}` : '',
      },
      proofPoints: proofList.map(s => s.trim()).filter(Boolean).join('\n'),
    };

    // Always save to localStorage immediately (no latency)
    saveSellerProfile(profileData);

    // Best-effort save to Supabase (fire-and-forget - user still navigates away)
    saveProfileToAPI(profileData).catch(() => {});

    navigate('/app');
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>

      {/* Header */}
      <header className={styles.header}>
        <button className={styles.logo} onClick={() => navigate('/')} title="Go to RepBuddy home" aria-label="RepBuddy home">
          {logoOk ? (
            <img
              className={styles.logoImg}
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="RepBuddy"
              onError={() => setLogoOk(false)}
            />
          ) : (
            <div className={styles.logoMark}>R</div>
          )}
          <span className={styles.logoName}>RepBuddy</span>
          <span className={styles.logoDivider} />
          <span className={styles.logoSub}>Seller Profile</span>
        </button>
        <button className={styles.backBtn} onClick={() => navigate('/app')}>⬅️ Back to Hunt</button>
      </header>

      <div className={styles.wrap}>

        {/* Hero */}
        <div className={styles.hero}>
          <div className={styles.heroIcon}>🎯</div>
          <div>
            <div className={styles.heroLabel}>Step 1: Seller Profile</div>
            <div className={styles.heroTitle}>Who are we hunting for?</div>
            <div className={styles.heroDesc}>
              Just the essentials: who you are, what you sell, and who you usually sell to. Everything else, like tone or a one-off filter, you can tell the chat as you go.
            </div>
          </div>
        </div>

        {/* ── Card: Seller Info ── */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <div className={styles.cardDot} />
            Seller Info
          </div>

          <div className={styles.grid2}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Seller Name <span className={styles.req}>*</span></label>
              <input className={styles.input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Gavin DeFord" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Company <span className={styles.req}>*</span></label>
              <input className={styles.input} value={company} onChange={e => setCompany(e.target.value)} placeholder="e.g. Revex" />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Title <span className={styles.req}>*</span></label>
            <input className={styles.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Co-Founder & Head of Sales" />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Company Website <span className={styles.labelHint}>(optional - let RepBuddy draft the fields below)</span>
            </label>
            <span className={styles.fieldHint}>
              Paste your company site. RepBuddy reads it (and your case-study pages) to draft your value proposition and proof points, then you review and edit everything before saving.
            </span>
            <div className={styles.genRow}>
              <input
                className={styles.input}
                value={website}
                onChange={e => setWebsite(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleGenerateFromWebsite(); } }}
                placeholder="e.g. repbuddy.io"
              />
              <button
                type="button"
                className={styles.genBtn}
                onClick={handleGenerateFromWebsite}
                disabled={genLoading || !website.trim()}
              >
                {genLoading ? 'Reading…' : '✨ Generate'}
              </button>
            </div>
            {genErr  && <div className={styles.genErr}>{genErr}</div>}
            {genNote && <div className={styles.genNote}>{genNote}</div>}
          </div>

          <div className={`${styles.field} ${styles.mb0}`}>
            <label className={styles.fieldLabel}>Value Proposition <span className={styles.req}>*</span></label>
            <span className={styles.fieldHint}>One sentence: what do you sell and why should they care?</span>
            <textarea
              className={styles.textarea}
              rows={2}
              value={valueProp}
              onChange={e => setValueProp(e.target.value)}
              placeholder="e.g. We help B2B founders fill their calendar with qualified meetings for a flat $400 per show-up"
            />
          </div>
        </div>

        {/* ── Card: ICP ── */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <div className={styles.cardDot} />
            Ideal Customer Profile
          </div>

          {/* Industries chip grid */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Target Industries
              {industries.length > 0 && (
                <span className={styles.stateCount}>{industries.length} selected</span>
              )}
            </label>
            <span className={styles.fieldHint}>
              Select one or more. The search API maps each selection to our standard industry taxonomy.
            </span>
            <div className={styles.stateSection}>
              <div className={styles.stateSectionHeader}>
                <span className={styles.stateSectionLabel}>Tech Verticals</span>
                {industries.length > 0 && (
                  <button className={styles.stateClearBtn} onClick={() => setIndustries([])}>Clear all</button>
                )}
              </div>
              <div className={styles.stateGrid}>
                {INDUSTRY_OPTIONS.slice(0, 18).map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`${styles.stateChip} ${industries.includes(key) ? styles.stateChipOn : ''}`}
                    onClick={() => toggleIndustry(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className={`${styles.stateSectionHeader} ${styles.stateSubHeader}`}>
                <span className={styles.stateSectionLabel}>Traditional Industries</span>
              </div>
              <div className={styles.stateGrid}>
                {INDUSTRY_OPTIONS.slice(18).map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`${styles.stateChip} ${industries.includes(key) ? styles.stateChipOn : ''}`}
                    onClick={() => toggleIndustry(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Job titles tag input */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Target Job Titles <span className={styles.labelHint}>(type + Enter, or paste a comma-separated list)</span>
            </label>
            <span className={styles.fieldHint}>e.g. CTO, VP of Engineering, Director of IT, Chief Information Officer</span>
            <div className={styles.tagInputWrap} onClick={() => document.getElementById('titlesInput').focus()}>
              <div className={styles.tags}>
                {jobTitles.map((t, i) => (
                  <span key={i} className={styles.tag}>
                    {t}
                    <span className={styles.tagX} onClick={e => { e.stopPropagation(); setJobTitles(p => p.filter((_, j) => j !== i)); }}>×</span>
                  </span>
                ))}
              </div>
              <input
                id="titlesInput"
                className={styles.tagInput}
                value={titlesInput}
                onChange={e => setTitlesInput(e.target.value)}
                onKeyDown={handleTitlesKey}
                onPaste={handleTitlesPaste}
                placeholder={jobTitles.length ? '' : 'Add job title, press Enter or paste comma-separated list…'}
              />
            </div>
          </div>

          {/* Company size */}
          <div className={`${styles.field} ${styles.mb0}`}>
            <label className={styles.fieldLabel}>Company Size (Employees)</label>
            <span className={styles.fieldHint}>Your usual range. Anything else (seniority, location, revenue, companies to skip) just say in chat for that search.</span>
            <div className={styles.rangeRow}>
              <select className={styles.select} value={companySizeMin} onChange={e => setCompanySizeMin(e.target.value)}>
                {SIZE_MIN_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span className={styles.rangeSep}>to</span>
              <select className={styles.select} value={companySizeMax} onChange={e => setCompanySizeMax(e.target.value)}>
                {SIZE_MAX_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Card: Proof Points ── */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <div className={styles.cardDot} />
            Proof Points
          </div>

          <div
            className={`${styles.pdfDrop} ${pdfLoading ? styles.pdfDropBusy : ''}`}
            onDragOver={e => e.preventDefault()}
            onDrop={handlePdfDrop}
            onClick={() => { if (!pdfLoading) pdfInputRef.current?.click(); }}
          >
            <span className={styles.pdfDropIcon}>📄</span>
            <span className={styles.pdfDropTitle}>
              {pdfLoading ? 'Reading your PDF…' : 'Drop a case-study PDF to pull proof points'}
            </span>
            <span className={styles.pdfDropSub}>Parsed in your browser. Only the text is sent, never the file.</span>
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className={styles.fileInput}
              onChange={handlePdfInput}
            />
          </div>
          {pdfErr  && <div className={styles.genErr}>{pdfErr}</div>}
          {pdfNote && <div className={styles.genNote}>{pdfNote}</div>}

          <div className={`${styles.field} ${styles.mb0}`}>
            <label className={styles.fieldLabel}>
              Real outcomes you can back up <span className={styles.labelHint}>(optional - used in email drafts instead of invented claims)</span>
            </label>
            <span className={styles.fieldHint}>One outcome per block. If left empty, drafts will skip social proof entirely rather than make something up.</span>
            <div className={styles.proofList}>
              {proofList.map((p, i) => (
                <div key={i} className={styles.proofRow}>
                  <textarea
                    className={styles.proofInput}
                    rows={2}
                    value={p}
                    onChange={e => updateProof(i, e.target.value)}
                    placeholder={i === 0
                      ? 'e.g. Email deliverability jumped because RepBuddy verified contacts before outreach.'
                      : 'Another outcome you can back up'}
                  />
                  <button
                    type="button"
                    className={styles.proofRemove}
                    onClick={() => removeProof(i)}
                    aria-label="Remove this proof point"
                    title="Remove"
                  >×</button>
                </div>
              ))}
            </div>
            <button type="button" className={styles.addProofBtn} onClick={addProof}>+ Add proof point</button>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.formFooter}>
          <button className={styles.btnGhost} onClick={clearForm}>Clear</button>
          <button className={styles.btnPrimary} onClick={handleSave}>Save Profile →</button>
        </div>

      </div>
    </div>
  );
}
